import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { GoogleMap, Marker, OverlayView, Polyline } from "@react-google-maps/api";

import MAP_STYLE from "../../home/components/mapStyle";
import { buildPinIcon } from "./mapMarkerIcon";

const CONTAINER_STYLE = { width: "100%", height: "100%" };

const FIT_PADDING = 40;

const SOLO_ZOOM = 14;

const MAX_FIT_ZOOM = 13;

const PIN_COLOR = "#AE6F28";

const MAP_OPTIONS = {
  styles: MAP_STYLE,
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
  keyboardShortcuts: false,
  gestureHandling: "cooperative",
};

const LINE_OPTIONS = {
  strokeColor: "#AE6F28",
  strokeOpacity: 0,
  icons: [
    {
      icon: { path: "M 0,-1 0,1", strokeOpacity: 0.7, scale: 3 },
      offset: "0",
      repeat: "12px",
    },
  ],
};

const SupplierMapCard = ({ name, coordinates, origin, mapsReady }) => {
  const mapRef = useRef(null);
  const cardRef = useRef(null);

  const centre = useMemo(
    () =>
      coordinates
        ? { lat: coordinates.latitude, lng: coordinates.longitude }
        : null,
    [coordinates]
  );

  const start = useMemo(
    () => (origin ? { lat: origin.latitude, lng: origin.longitude } : null),
    [origin]
  );

  const applyRegion = useCallback(() => {
    const map = mapRef.current;
    const maps = window.google?.maps;

    if (!map || !maps || !centre) return;

    if (!start) {
      map.setCenter(centre);
      map.setZoom(SOLO_ZOOM);

      return;
    }

    const bounds = new maps.LatLngBounds();

    bounds.extend(centre);
    bounds.extend(start);

    map.fitBounds(bounds, FIT_PADDING);

    maps.event.addListenerOnce(map, "bounds_changed", () => {
      if (map.getZoom() > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
    });
  }, [centre, start]);

  useEffect(() => {
    applyRegion();
  }, [applyRegion]);

  useEffect(() => {
    const node = cardRef.current;
    if (!node || typeof ResizeObserver !== "function") return undefined;

    const observer = new ResizeObserver(() => applyRegion());

    observer.observe(node);

    return () => observer.disconnect();
  }, [applyRegion]);

  if (!centre || !mapsReady) {
    return (
      <div className="sy-map-card sy-map-card-empty">
        <p>
          {centre
            ? "Loading the map…"
            : "This supplier has not shared a location yet."}
        </p>
      </div>
    );
  }

  return (
    <div className="sy-map-card" ref={cardRef}>
      <GoogleMap
        center={centre}
        zoom={SOLO_ZOOM}
        options={MAP_OPTIONS}
        mapContainerStyle={CONTAINER_STYLE}
        onLoad={(map) => {
          mapRef.current = map;
          applyRegion();
        }}
        onUnmount={() => {
          mapRef.current = null;
        }}
      >
        <Marker position={centre} title={name} icon={buildPinIcon(PIN_COLOR)} />

        {start && (
          <>
            <OverlayView
              position={start}
              mapPaneName={OverlayView.OVERLAY_LAYER}
              getPixelPositionOffset={(width, height) => ({
                x: -(width / 2),
                y: -(height / 2),
              })}
            >
              <span className="home-map-user-dot" />
            </OverlayView>

            <Polyline path={[start, centre]} options={LINE_OPTIONS} />
          </>
        )}
      </GoogleMap>
    </div>
  );
};

export default React.memo(SupplierMapCard);
